/**
 * Загрузка картинок песочницы `/generate` (референсы, фото платья): `POST /api/fittings/uploads`.
 * Битый файл не отменяет остальные — он приходит строкой в `errors`.
 */
import { apiFetch } from "@/lib/api";

/** Размеры известны не всегда: у картинок, восстановленных из ленты, их нет. */
export type Upload = { asset_id: number; path: string; width?: number | null; height?: number | null };

/**
 * Роль вставленной картинки: `hint` — прежняя подсказка «что взять / что не брать» (умолчание),
 * `portrait` — внешность модели (её лицо, причёска и типаж). Роль есть только у вставленных
 * картинок блока «Референсы»; у загрузок фото платья её нет — они идут во входы товаром.
 */
export type PicRole = "hint" | "portrait";

/** Картинка песочницы на странице: комментарий для модели, галочка (у референсов всегда включена) и роль. */
export type Pic = Upload & { comment: string; on: boolean; role?: PicRole };

/** За раз сервис берёт не больше стольких файлов. */
export const UPLOADS_MAX = 10;
/** Комментарий к картинке — не длиннее (как `EXTRA_COMMENT_MAX`). */
export const COMMENT_MAX = 300;

export async function uploadImages(files: File[]): Promise<{ items: Upload[]; errors: string[] }> {
  const items: Upload[] = [];
  const errors: string[] = [];
  for (let i = 0; i < files.length; i += UPLOADS_MAX) {
    const form = new FormData();
    for (const f of files.slice(i, i + UPLOADS_MAX)) form.append("files", f, f.name || "image.png");
    const got = await apiFetch<{ items: Upload[]; errors: string[] }>("/api/fittings/uploads", { method: "POST", body: form });
    items.push(...got.items);
    errors.push(...got.errors);
  }
  return { items, errors };
}

export const toPic = (u: Upload): Pic => ({ ...u, comment: "", on: true });

/** Добавляет картинки без дублей по `asset_id`: повторная вставка того же файла не плодит плитку. */
export const addPics = (have: Pic[], more: Pic[]): Pic[] => {
  const out = [...have];
  for (const p of more) if (!out.some((x) => x.asset_id === p.asset_id)) out.push(p);
  return out;
};

/** Картинки из события drop/вставки/выбора файла. */
export const imageFiles = (list: FileList | File[] | null | undefined): File[] =>
  Array.from(list ?? []).filter((f) => f.type.startsWith("image/"));
